/* eslint-disable comma-dangle */
import * as tomSelect from "/Assets/libs/TomSelect/tom-select.complete.js";
import { getCurrentLanguage, getWithExpiry, isNullOrUndefined } from "/Assets/js/utilities.js";
import InputMask from "/Assets/libs/input-mask.js";

// Select types with specific initializations - country, stateProv, productType, productName
// All others follow default initialization

document.addEventListener("DOMContentLoaded", () => {
    document.querySelectorAll("[data-select-element]").forEach(element => {
        new SelectionElement(element);
    });
});

class SelectionElement {
    constructor(element) {
        this.element = element;
        this.form = this.element.closest("form");
        this.formName = this.form.getAttribute("name");
        this.serialNumberInput = this.form.querySelector("div.serialNumber input");
        this.addFormatMask = this.addFormatMask.bind(this);
        this.addPrefixValue = this.addPrefixValue.bind(this);
        this.initializeSelect();
    }

    initializeSelect() {
        const selectType = this.element.dataset.selectType;
        const options = this.element.querySelectorAll("option");

        if (options.length === 0) {
            return;
        }

        this.setOriginalPlaceholder();

        switch (selectType) {
            case "product_interest_text": // this is being initialized in OptimizelyForms class
                break;
            case "ht_countryisolookup":
            case "country":
                this.initializeCountrySelect(this.element);
                break;
            case "productType":
                this.initializeSelectProductTypeSelect(this.element);
                break;
            case "productName": {
                const productType = this.form.querySelector("div.productType").getAttribute("data-selected-producttype");
                this.initializeProductSelect(this.element, productType);
                break;
            }
            case "ht_address1subdivisioniso": {
                const country = this.form.querySelector("div.ht_countryisolookup select").value;
                this.initializeStateSelect(this.element, country);
                break;
            }
            case "stateProv": {
                const country = this.form.querySelector("div.country select").value;
                this.initializeStateSelect(this.element, country);
                break;
            }
            default:
                if (this.element.hasAttribute("multiple")) {
                    new tomSelect(this.element, {
                        plugins: {
                            remove_button: {
                                title: "Remove this item",
                            }
                        }
                    });
                } else {
                    new tomSelect(this.element, {});
                }

                break;
        }
    }

    initializeSelectProductTypeSelect(element) {
        const selectedProductTypeValue = element.closest("div.productType").getAttribute("data-selected-producttype");
        const selectedProductType = selectedProductTypeValue ? [selectedProductTypeValue] : [];

        new tomSelect(element, {
            items: selectedProductType,
            onChange: value => {
                const productSelect = this.form.querySelector("div.productName select");
                if (productSelect) {
                    if (productSelect.tomselect) {
                        productSelect.tomselect.clear();
                    }
                    this.initializeProductSelect(productSelect, value);
                    this.clearSerialNumberElement();
                }
            }
        });
    }

    async initializeProductSelect(element, productType) {
        const productSelectBlockId = element.closest("div.productName").getAttribute("data-block-id");
        const selectedProductValue = element.closest("div.productName").getAttribute("data-selected-product");
        const selectedProduct = selectedProductValue ? [selectedProductValue] : [];

        if (isNullOrUndefined(productType)) {
            productType = "notype";
        }

        const url = `/global/products?epslanguage=${getCurrentLanguage()}&type=${productType}&id=${productSelectBlockId}&form=${this.formName}`;
        try {
            const response = await fetch(url);
            const json = await response.json();
            let tomselect = element.tomselect;
            if (tomselect) {
                tomselect.clearOptions();
                tomselect.addOption(json);
            } else {
                new tomSelect(element, {
                    items: selectedProduct,
                    maxOptions: null,
                    valueField: "code",
                    labelField: "name",
                    options: json,
                    onChange: value => {
                        this.loadSerialNumberFormatMasks(value);
                    },
                    onInitialize: () => {
                        this.setOriginalPlaceholder();
                    }
                });
            }
        } catch (error) {
            console.error("Error initializing product select:", error);
        }
    }

    initializeCountrySelect(element) {
        const countrySelect = new tomSelect(element, {
            maxOptions: null,
            onChange: value => {
                let stateSelect = this.form.querySelector("div.stateProv select");
                if (!stateSelect) {
                    stateSelect = this.form.querySelector("div.ht_address1subdivisioniso select");
                }
                if (stateSelect) {
                    if (stateSelect.tomselect) {
                        stateSelect.tomselect.clear();
                    }
                    this.initializeStateSelect(stateSelect, value);
                }
            }
        });

        const form = element.closest("form");
        const formName = form.getAttribute("name");
        if (formName === "catridge-return-to-recycle") {
            countrySelect.setValue("US");
            countrySelect.disable();
        }
    }

    async initializeStateSelect(element, country) {
        //if no country just initialize an empty select
        if (isNullOrUndefined(country)) {
            new tomSelect(element, {});
        }

        const includeCountries = ["US", "CA", "AU", "NZ", "CN"];
        const shouldGetProvinces = includeCountries.includes(country);

        if (!shouldGetProvinces) {
            let tomselect = element.tomselect;
            if (tomselect) {
                tomselect.clear();
                tomselect.clearOptions();
            }
            return;
        }

        const url = `/provinces/provinces?countryCode=${country}&language=${getCurrentLanguage()}`;
        try {
            const response = await fetch(url);
            const items = await response.json();

            const select = element;
            select.innerHTML = "";

            const optionElement = document.createElement("option");
            optionElement.value = "";
            optionElement.text = element.getAttribute("placeholder");
            select.add(optionElement);

            items.forEach(item => {
                const itemVal = item.code === "Other" ? item.name : item.code;
                const optionElement = document.createElement("option");
                optionElement.value = itemVal;
                optionElement.text = item.name;
                select.add(optionElement);
            });

            let tomselect = element.tomselect;
            if (tomselect) {
                tomselect.clear();
                tomselect.clearOptions();
                tomselect.sync();
            } else {
                new tomSelect(element, {
                    maxOptions: null
                });
            }
        } catch (error) {
            console.error("Error initializing state select:", error);
        }
    }

    setOriginalPlaceholder() {
        if (!this.serialNumberInput) return;

        const originalPlaceholder = this.serialNumberInput.getAttribute("placeholder");
        this.serialNumberInput.setAttribute("originalPlaceholder", originalPlaceholder);
    }

    clearSerialNumberElement() {
        this.serialNumberInput.removeEventListener("input", this.addFormatMask);
        this.serialNumberInput.value = "";
        this.serialNumberInput.setAttribute("placeholder", this.serialNumberInput.getAttribute("originalPlaceholder"));
    }

    loadSerialNumberFormatMasks(product) {
        if (!this.serialNumberInput) return;

        this.clearSerialNumberElement();

        this.serialNumberInput.removeEventListener("focus", this.addPrefixValue);
        this.serialNumberInput.removeEventListener("input", this.addFormatMask);

        const formatMasksCacheKey = "product-serial-number-format-masks";
        const formatMasks = JSON.parse(getWithExpiry(formatMasksCacheKey));

        if (formatMasks) {
            const asLowercase = product.toLowerCase();
            const productMask = formatMasks[Object.keys(formatMasks).find(key => key.toLowerCase() === asLowercase)];

            if (productMask) {
                const maskPlaceholder = productMask.replace(/\*/g, "_ ");
                this.serialNumberInput.setAttribute("placeholder", maskPlaceholder);

                const maskPrefix = productMask.replace(/\*/g, "");
                this.serialNumberInput.setAttribute("data-mask-prefix", maskPrefix);

                const asteriskCount = (productMask.match(/\*/g) || []).length;
                const mask = "0".repeat(asteriskCount);
                this.serialNumberInput.setAttribute("data-mask", mask);

                this.serialNumberInput.addEventListener("focus", this.addPrefixValue);
                this.serialNumberInput.addEventListener("input", this.addFormatMask);
            }
        }
    }

    addPrefixValue() {
        const prefix = this.serialNumberInput.getAttribute("data-mask-prefix");
        this.serialNumberInput.value = prefix;
    }

    addFormatMask(e) {
        const inputMask = new InputMask();
        inputMask.maskInput(e);
    }
}
